/**
 * The chord data model.
 *
 * Two layers, deliberately kept apart:
 *
 *  - `ChordSpec` is *structural and spelling-bearing*. It says "a minor triad on
 *    E flat with a minor seventh". It is what a human writes, what the LLM emits,
 *    and what survives a change of key context unchanged in meaning.
 *  - `CanonicalChord` is `ChordSpec` plus everything derived from it — most
 *    importantly `pitchClasses`, which is the ONLY thing the recogniser and the
 *    branch matcher compare. Spelling never participates in matching.
 *
 * The recogniser emits pitch classes; `respell()` assigns letters at session level
 * against the current `KeyEstimate`. The same detected sonority is therefore
 * "E♭" in a flat context and "D♯" in B major, without any re-detection.
 */

/** Chromatic pitch class. 0 = C, 1 = C♯/D♭, … 11 = B. */
export type PitchClass = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

/** A staff letter name. */
export type Letter = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

/** A letter plus an accidental. -2 = double flat, -1 = flat, 0 = natural, 1 = sharp, 2 = double sharp. */
export interface SpelledNote {
  letter: Letter;
  accidental: -2 | -1 | 0 | 1 | 2;
}

/**
 * The triad (or dyad) at the base of the chord.
 *
 * `power` is a bare root+fifth with no third at all — distinct from `omissions: ['3']`,
 * which means "a triad of known quality whose third happens not to be sounding".
 */
export type TriadQuality =
  'major' | 'minor' | 'diminished' | 'augmented' | 'sus2' | 'sus4' | 'power';

/**
 * The interval of the seventh above the root, NOT the quality of the whole chord.
 * `maj7` = 11 semitones, `min7` = 10, `dim7` = 9.
 *
 * So a dominant seventh is `{ quality: 'major', seventh: 'min7' }`, a half-diminished
 * seventh is `{ quality: 'diminished', seventh: 'min7' }`, and a fully diminished
 * seventh is `{ quality: 'diminished', seventh: 'dim7' }`.
 */
export type SeventhType = 'maj7' | 'min7' | 'dim7';

/** Colour tones above (or, for `6`, beside) the seventh. */
export type Extension = '6' | '9' | 'b9' | '#9' | '11' | '#11' | '13' | 'b13';

/**
 * Alterations of the fifth that do not change the triad quality.
 *
 * Note the normalisation performed by `parseChordSymbol` and `toCanonical`:
 * minor + `b5` collapses to `diminished`, and major + `#5` collapses to `augmented`,
 * because those are the same sonority and one representation must win. `b5` therefore
 * only ever survives on a major/sus base (e.g. C7♭5) and `#5` on a minor/sus base.
 */
export type Alteration = 'b5' | '#5';

/** Structural, spelling-bearing chord description. */
export interface ChordSpec {
  root: SpelledNote;
  quality: TriadQuality;
  seventh?: SeventhType;
  extensions: Extension[];
  alterations: Alteration[];
  omissions: Array<'3' | '5'>;
  /** Present only when the sounding bass is not the root. */
  bass?: SpelledNote;
}

/**
 * `ChordSpec` plus its derived form. Produced by `toCanonical()`; this is the
 * currency the whole application passes around.
 */
export interface CanonicalChord extends ChordSpec {
  /**
   * Sorted ascending and deduped. THE matching key — every comparison between a
   * detection and a suggestion goes through this array, never through `symbol`.
   * Includes `bassPc` even when the bass is not a chord tone (C/D → [0, 2, 4, 7]),
   * because the bass is sounding and the recogniser will hear it.
   */
  pitchClasses: PitchClass[];
  rootPc: PitchClass;
  /** Equals `rootPc` in root position. */
  bassPc: PitchClass;
  /**
   * 0 = root position, 1 = third in the bass, 2 = fifth in the bass, 3 = seventh in
   * the bass. A bass note that is not a chord tone at all (a pedal, C/D) reports 0.
   */
  inversion: 0 | 1 | 2 | 3;
  /** Formatter output, e.g. "Cm7/E♭". Display only — never parse this back for matching. */
  symbol: string;
}

/** Semitone offset of each natural letter above C. */
export const LETTER_PITCH_CLASSES: Readonly<Record<Letter, PitchClass>> = Object.freeze({
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
});

/** Letters in ascending scale order from C, so that thirds are index steps of 2. */
export const LETTERS_FROM_C: readonly Letter[] = Object.freeze([
  'C',
  'D',
  'E',
  'F',
  'G',
  'A',
  'B',
] as const);

/**
 * Position of each natural letter on the line of fifths, taking C = 0.
 * Every sharp adds 7, every flat subtracts 7.
 */
export const LETTER_LINE_OF_FIFTHS: Readonly<Record<Letter, number>> = Object.freeze({
  F: -1,
  C: 0,
  G: 1,
  D: 2,
  A: 3,
  E: 4,
  B: 5,
});

/** Canonical ordering for `Extension[]`, so two equal chords always compare equal. */
export const EXTENSION_ORDER: readonly Extension[] = Object.freeze([
  '6',
  '9',
  'b9',
  '#9',
  '11',
  '#11',
  '13',
  'b13',
] as const);

/** Canonical ordering for `Alteration[]`. */
export const ALTERATION_ORDER: readonly Alteration[] = Object.freeze(['b5', '#5'] as const);

/** Semitone above the root for each extension. */
export const EXTENSION_SEMITONES: Readonly<Record<Extension, number>> = Object.freeze({
  '6': 9,
  '9': 2,
  b9: 1,
  '#9': 3,
  '11': 5,
  '#11': 6,
  '13': 9,
  b13: 8,
});

/**
 * Which numbered chord degree each extension occupies. Used to enforce "an altered
 * ninth replaces the natural ninth" and to build legal third-stacks when respelling.
 */
export const EXTENSION_DEGREES: Readonly<Record<Extension, 6 | 9 | 11 | 13>> = Object.freeze({
  '6': 6,
  '9': 9,
  b9: 9,
  '#9': 9,
  '11': 11,
  '#11': 11,
  '13': 13,
  b13: 13,
});

/** Semitones from the root to the third for each triad quality (`power` has none). */
export const QUALITY_THIRD_SEMITONES: Readonly<Record<TriadQuality, number | null>> = Object.freeze(
  {
    major: 4,
    minor: 3,
    diminished: 3,
    augmented: 4,
    sus2: 2,
    sus4: 5,
    power: null,
  },
);

/** Semitones from the root to the fifth for each triad quality. */
export const QUALITY_FIFTH_SEMITONES: Readonly<Record<TriadQuality, number>> = Object.freeze({
  major: 7,
  minor: 7,
  diminished: 6,
  augmented: 8,
  sus2: 7,
  sus4: 7,
  power: 7,
});

/** Semitones from the root to the seventh. */
export const SEVENTH_SEMITONES: Readonly<Record<SeventhType, number>> = Object.freeze({
  maj7: 11,
  min7: 10,
  dim7: 9,
});

/** A numbered position in a stack of thirds. */
export type ChordDegree = 1 | 3 | 5 | 6 | 7 | 9 | 11 | 13;

/**
 * How many letter-steps above the root letter each degree sits.
 * Degree 9 is a second (1 step) an octave up, 11 a fourth (3 steps), 13 a sixth (5 steps).
 */
export const DEGREE_LETTER_STEPS: Readonly<Record<ChordDegree, number>> = Object.freeze({
  1: 0,
  3: 2,
  5: 4,
  6: 5,
  7: 6,
  9: 1,
  11: 3,
  13: 5,
});
